import { useState, useRef } from "react";
import axios from "axios";
import { Capacitor } from "@capacitor/core";
import { SpeechRecognition } from "@capacitor-community/speech-recognition";
import { BASE_API_URL } from "../../utils/consts";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export interface PaymentBreakdown {
  type: string;
  label: string;
  total: number;
  percentage: string;
}

export interface DynamicCategory {
  id: string;
  name: string;
  color: string;
  total: number;
  percentage: string;
}

export interface ExpenseCategory {
  name: string;
  total: number;
}

export interface QueryMeta {
  period: string;
  startDate: string;
  endDate: string;
  locationName: string | null;
}

export interface RevenueResult {
  intent: "revenue";
  query: QueryMeta;
  data: {
    totalRevenue: number;
    extraIncomeTotal: number;
    staticPayments: PaymentBreakdown[];
    dynamicCategories: DynamicCategory[];
  };
  warnings: string[];
}

export interface ExpensesResult {
  intent: "expenses";
  query: QueryMeta;
  data: {
    totalExpenses: number;
    customValuesTotal: number;
    customValuesEurTotal: number;
    dailyExpensesTotal: number;
    deductionsTotal: number;
    categories: ExpenseCategory[];
  };
  warnings: string[];
}

export interface ProfitResult {
  intent: "profit";
  query: QueryMeta;
  data: {
    totalRevenue: number;
    totalExpenses: number;
    profit: number;
    profitMargin: string;
    revenue: {
      staticPayments: PaymentBreakdown[];
      dynamicCategories: DynamicCategory[];
    };
    expenses: {
      customValuesTotal: number;
      customValuesEurTotal: number;
      dailyExpensesTotal: number;
      deductionsTotal: number;
      categories: ExpenseCategory[];
    };
  };
  warnings: string[];
}

export interface SalaryRates {
  hourlyRates?: number;
  percentageRates?: number;
  dailyWages?: number;
  fixedSalaries?: number;
  temporaryHelps?: number;
  travelExpenses?: number;
}

export interface LocationRates {
  locationName: string | null;
  rates: SalaryRates;
}

export interface EmployeeRateResult {
  intent: "employee_rate";
  data: {
    employeeName: string;
    ratesByLocation: LocationRates[];
  };
  warnings: string[];
}

export interface EmployeeSalaryResult {
  intent: "employee_salary";
  query: {
    period: string;
    startDate: string;
    endDate: string;
  };
  data: {
    employeeName: string;
    shiftCount: number;
    totalHours: number;
    grossSalary: number;
    loanDeduction: number;
    totalForMonth: number;
    fixed: number;
    daily: number;
    percentage: number;
    bonus: number;
    overtime: number;
    penalty: number;
    payoutToBank: number;
    cashAmount: number;
  };
  warnings: string[];
}

export interface AnalysisResult {
  intent: "analysis";
  analysisType: "overview" | "comparison" | "suggestions";
  analysisText: string;
  query?: QueryMeta;
  data: {
    revenue?: { totalRevenue: number; staticPayments: PaymentBreakdown[]; dynamicCategories: DynamicCategory[] };
    expenses?: { totalExpenses: number; categories: ExpenseCategory[] };
    periods?: Array<{
      label: string;
      revenue: { totalRevenue: number };
      expenses: { totalExpenses: number };
    }>;
  };
  warnings: string[];
}

export type AssistantResult = RevenueResult | ExpensesResult | ProfitResult | EmployeeRateResult | EmployeeSalaryResult | AnalysisResult;

export interface ReservationPreview {
  guestName: string;
  guestPhone: string | null;
  date: string;       // "YYYY-MM-DD"
  time: string;       // "HH:mm"
  numberOfGuests: number;
  note: string | null;
}

export type VoiceAssistantState =
  | { status: "idle" }
  | { status: "listening"; interimTranscript: string }
  | { status: "parsing"; transcript: string }
  | { status: "result"; result: AssistantResult }
  | { status: "reservation_preview"; data: ReservationPreview }
  | { status: "saving_reservation" }
  | { status: "error"; message: string };

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
export function useVoiceAssistant(clientId: string) {
  const [state, setState] = useState<VoiceAssistantState>({ status: "idle" });
  const [lang, setLang] = useState<"bcms" | "sl" | "en">("bcms");
  const recognitionRef = useRef<any>(null);
  const finalTranscriptRef = useRef<string>("");

  const startListening = () => {
    if (Capacitor.isNativePlatform()) {
      startListeningNative();
    } else {
      startListeningWeb();
    }
  };

  const startListeningNative = async () => {
    finalTranscriptRef.current = "";
    setState({ status: "listening", interimTranscript: "" });

    try {
      const { available } = await SpeechRecognition.available();
      if (!available) {
        setState({ status: "error", message: "Prepoznavanje govora nije dostupno na ovom uređaju." });
        return;
      }

      const perm = await SpeechRecognition.requestPermissions();
      if (perm.speechRecognition !== "granted") {
        setState({ status: "error", message: "Pristup mikrofonu odbijen. Dozvoli pristup u Podešavanja → Privatnost → Mikrofon." });
        return;
      }

      const language =
        lang === "en" ? "en_US" :
        lang === "sl" ? "sl_SI" :
        await pickBestSerbianLocale();
      if (!language) {
        setState({ status: "error", message: "Glasovni unos nije dostupan. Pokušaj dodati Hrvatski (hr) u Podešavanja → Opšte → Jezik i region." });
        return;
      }

      await SpeechRecognition.addListener("partialResults", (data: { matches: string[] }) => {
        const text = data.matches?.[0] ?? "";
        finalTranscriptRef.current = text;
        setState({ status: "listening", interimTranscript: text });
      });

      // listeningState: fired when recognition ends naturally (silence/timeout)
      await SpeechRecognition.addListener("listeningState", (data: { status: string }) => {
        if (data.status === "stopped" && recognitionRef.current === "native") {
          recognitionRef.current = null;
          SpeechRecognition.removeAllListeners();
          const transcript = finalTranscriptRef.current.trim();
          if (transcript) sendQuery(transcript);
          else setState({ status: "idle" });
        }
      });

      recognitionRef.current = "native";

      // start() se razriješi odmah kada je partialResults:true —
      // rezultati dolaze asinhorno kroz partialResults i listeningState evente
      await SpeechRecognition.start({
        language,
        maxResults: 1,
        partialResults: true,
        popup: false,
      });
    } catch (err: any) {
      await SpeechRecognition.removeAllListeners();
      recognitionRef.current = null;
      setState({ status: "error", message: "Nije moguće pokrenuti prepoznavanje govora. Pokušaj ponovo." });
    }
  };

  const startListeningWeb = () => {
    const WebSpeechRecognition =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;

    if (!WebSpeechRecognition) {
      setState({ status: "error", message: "Voice input is not supported in this browser. Please use Chrome or Edge." });
      return;
    }

    finalTranscriptRef.current = "";
    const recognition = new WebSpeechRecognition();
    recognition.lang = lang === "en" ? "en-US" : lang === "sl" ? "sl-SI" : "hr-HR";
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    recognitionRef.current = recognition;
    setState({ status: "listening", interimTranscript: "" });

    recognition.onresult = (event: any) => {
      let interim = "";
      let newFinal = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const text = event.results[i][0].transcript;
        if (event.results[i].isFinal) newFinal += text + " ";
        else interim += text;
      }
      if (newFinal) finalTranscriptRef.current += newFinal;
      setState({ status: "listening", interimTranscript: (finalTranscriptRef.current + interim).trim() });
    };

    recognition.onerror = (event: any) => {
      recognitionRef.current = null;
      if (event.error === "not-allowed" || event.error === "service-not-allowed") {
        setState({ status: "error", message: "Pristup mikrofonu odbijen. Dozvoli pristup mikrofonu u podešavanjima browsera." });
      } else if (event.error === "audio-capture") {
        setState({ status: "error", message: "Mikrofon nije dostupan. Provjeri da li je priključen i dozvoljen." });
      } else if (event.error !== "aborted") {
        setState({ status: "error", message: "Nije moguće snimiti glas. Pokušaj ponovo." });
      }
    };

    recognition.onend = () => {
      recognitionRef.current = null;
      const transcript = finalTranscriptRef.current.trim();
      if (transcript) sendQuery(transcript);
      else setState({ status: "idle" });
    };

    recognition.start();
  };

  const stopListening = async () => {
    if (recognitionRef.current === "native") {
      recognitionRef.current = null;
      await SpeechRecognition.stop();
      await SpeechRecognition.removeAllListeners();
      const transcript = finalTranscriptRef.current.trim();
      if (transcript) sendQuery(transcript);
      else setState({ status: "idle" });
    } else {
      recognitionRef.current?.stop();
    }
  };

  const sendQuery = async (transcript: string, inputType: "voice" | "text" = "voice") => {
    setState({ status: "parsing", transcript });
    try {
      const { data } = await axios.post(`${BASE_API_URL}/voice-assistant/query`, {
        transcript,
        clientId,
        currentDate: new Date().toISOString(),
        inputType,
      });
      if (data.intent === "reservation") {
        setState({ status: "reservation_preview", data: data.data as ReservationPreview });
      } else {
        setState({ status: "result", result: data as AssistantResult });
      }
    } catch (err: any) {
      setState({
        status: "error",
        message: err.response?.data?.message || "Failed to process query. Try: 'Prikaži zaradu za februar'",
      });
    }
  };

  const confirmReservation = async (preview: ReservationPreview, guestPhone: string): Promise<boolean> => {
    setState({ status: "saving_reservation" });
    try {
      await axios.post(`${BASE_API_URL}/${clientId}/reservations`, {
        guestName: preview.guestName,
        guestPhone,
        date: preview.date,
        time: preview.time,
        numberOfGuests: preview.numberOfGuests,
        note: preview.note,
        source: "internal",
        status: "confirmed",
      });
      setState({ status: "idle" });
      return true;
    } catch (err: any) {
      setState({
        status: "error",
        message: err.response?.data?.error || "Greška pri kreiranju rezervacije. Pokušaj ponovo.",
      });
      return false;
    }
  };

  const reset = async () => {
    if (recognitionRef.current === "native") {
      await SpeechRecognition.stop().catch(() => {});
      await SpeechRecognition.removeAllListeners().catch(() => {});
    } else if (recognitionRef.current) {
      recognitionRef.current.abort();
    }
    recognitionRef.current = null;
    finalTranscriptRef.current = "";
    setState({ status: "idle" });
  };

  return { state, lang, setLang, startListening, stopListening, sendQuery, confirmReservation, reset };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
async function pickBestSerbianLocale(): Promise<string | null> {
  try {
    const { languages } = await SpeechRecognition.getSupportedLanguages();
    const langs = languages as string[];
    // iOS locale identifiers koriste underscore: sr_RS, hr_HR itd.
    // Pokušaj srpski, pa hrvatski (isti govorni jezik — Apple podržava hr_HR)
    const preferred = ["sr_RS", "sr-RS", "sr_Latn_RS", "sr-Latn-RS", "sr", "hr_HR", "hr-HR", "hr", "en_US", "en-US", "en_GB", "en-GB", "en"];
    for (const p of preferred) {
      if (langs.includes(p)) return p;
    }
    const any = langs.find((l) => l.startsWith("sr") || l.startsWith("hr") || l.startsWith("en"));
    if (any) return any;
  } catch {}
  return null;
}
